import  { useState, useEffect } from 'react';
import { awaitingSubStatuses, inFacilitySubStatuses, destinationWarehouseSubStatuses, distributionCenterSubStatuses } from './SubStatusConfig';
import NotificationIcons from './NotificationIcons';

const fulfillmentStatuses = [
  { 
    label: 'Awaiting', 
    icon: '/img/waiting-time.svg', 
    value: 'awaiting'
  },
  { 
    label: 'In Facility', 
    icon: '/img/facility.svg', 
    value: 'in_facility'
  },
  { 
    label: 'Destination Warehouse', 
    icon: '/img/destination.svg', 
    value: 'destination_warehouse'
  },
  { 
    label: 'Distribution Center', 
    icon: '/img/distribution-center.svg', 
    value: 'distribution_center'
  },
  { 
    label: 'Delivered', 
    icon: '/img/shopping-bag.svg', 
    value: 'delivered'
  },
];

export default function FulfillmentTabs({ selected, selectedSubStatus, onSelect, onSubStatusSelect }: {
  selected?: string;
  selectedSubStatus?: string;
  onSelect?: (status: string) => void; 
  onSubStatusSelect?: (subStatus: string) => void;
}) {
  const [activeStatus, setActiveStatus] = useState(selected || 'awaiting');
  const [activeAwaitingSubStatus, setActiveAwaitingSubStatus] = useState(selectedSubStatus || 'on_its_way');
  const [activeInFacilitySubStatus, setActiveInFacilitySubStatus] = useState(selectedSubStatus || 'in_bound');
  const [activeDestinationWarehouseSubStatus, setActiveDestinationWarehouseSubStatus] = useState(selectedSubStatus || 'inbound');
  const [activeDistributionCenterSubStatus, setActiveDistributionCenterSubStatus] = useState(selectedSubStatus || 'inbound');

  // Update internal state when props change
  useEffect(() => {
    if (selected) {
      setActiveStatus(selected);
    }
  }, [selected]);

  useEffect(() => {
    if (selectedSubStatus) {
      if (selected === 'awaiting') {
        setActiveAwaitingSubStatus(selectedSubStatus);
      } else if (selected === 'in_facility') {
        setActiveInFacilitySubStatus(selectedSubStatus);
      } else if (selected === 'destination_warehouse') {
        setActiveDestinationWarehouseSubStatus(selectedSubStatus);
      } else if (selected === 'distribution_center') {
        setActiveDistributionCenterSubStatus(selectedSubStatus);
      }
    }
  }, [selectedSubStatus, selected]);

  const handleStatusClick = (statusValue: string) => {
    setActiveStatus(statusValue);
    onSelect?.(statusValue);
  };

  const handleAwaitingSubStatusClick = (subStatusValue: string) => {
    setActiveAwaitingSubStatus(subStatusValue);
    onSubStatusSelect?.(subStatusValue);
  };

  const handleInFacilitySubStatusClick = (subStatusValue: string) => {
    setActiveInFacilitySubStatus(subStatusValue);
    onSubStatusSelect?.(subStatusValue);
  };

  const handleDestinationWarehouseSubStatusClick = (subStatusValue: string) => {
    setActiveDestinationWarehouseSubStatus(subStatusValue);
    onSubStatusSelect?.(subStatusValue);
  };

  const handleDistributionCenterSubStatusClick = (subStatusValue: string) => {
    setActiveDistributionCenterSubStatus(subStatusValue);
    onSubStatusSelect?.(subStatusValue);
  };

  return (
    <div className="space-y-4">
      {/* Main Status Tabs */}
      <div className="flex justify-between items-center mb-6 mt-[-24px] overflow-x-auto py-3 bg-gray-200 px-8 outline outline-1 outline-gray-200 relative z-10">
        <div className="flex gap-3">
          {fulfillmentStatuses.map((status) => {
            const isSelected = activeStatus === status.value;
            return (
              <button
                key={status.value}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer
                  ${isSelected
                    ? 'text-primary-500'
                    : 'text-gray-600 hover:text-primary-500/40'}
                `}
                onClick={() => handleStatusClick(status.value)}
              >
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                  isSelected ? 'bg-primary-500' : 'bg-white hover:bg-gray-50'
                }`}>
                  <img
                    src={status.icon}
                    alt={status.label}
                    className="w-6 h-6"
                  />
                </div>
                <span>
                  {status.label === 'Destination Warehouse' ? (
                    <>Destination<br />Warehouse</>
                  ) : status.label === 'Distribution Center' ? (
                    <>Distribution<br />Center</>
                  ) : (
                    status.label
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {/* Notification Icons */}
        <NotificationIcons />
      </div>

      {/* Sub-Status Filters - Only show when Awaiting is selected */}
      {activeStatus === 'awaiting' && (
        <div className="px-8 mt-4 relative z-0">
          <div className="flex mt-[-30px] mb-8 ml-[13px] w-fit relative z-0">
            {awaitingSubStatuses.map((subStatus, index) => {
              const isSelected = activeAwaitingSubStatus === subStatus.value;
              const overlapClass = index === 0 ? '' : '-ml-6';
              
              return (
                <button
                  key={subStatus.value}
                  className={`${overlapClass} px-8 py-2.5 first:pl-7 last:pr-7 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-full border relative
                    ${isSelected 
                      ? 'bg-[#FFF8D6] text-[#D4A017] font-semibold border-[#F2BC00]' 
                      : 'bg-white text-[#1E3255] border-[#4B5675] hover:bg-[#F7F9FC]'
                    }
                  `}
                  style={{ zIndex: 20 - index }}
                  onClick={() => handleAwaitingSubStatusClick(subStatus.value)}
                >
                  {subStatus.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sub-Status Filters - Only show when In Facility is selected */}
      {activeStatus === 'in_facility' && (
        <div className="px-8 mt-4 relative z-0">
          <div className="flex mt-[-30px] mb-8 ml-[100px] w-fit relative z-0">
            {inFacilitySubStatuses.map((subStatus, index) => {
              const isSelected = activeInFacilitySubStatus === subStatus.value;
              const overlapClass = index === 0 ? '' : '-ml-6';
              
              return (
                <button
                  key={subStatus.value}
                  className={`${overlapClass} px-8 py-2.5 first:pl-7 last:pr-7 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-full border relative
                    ${isSelected 
                      ? 'bg-[#FFF8D6] text-[#D4A017] font-semibold border-[#F2BC00]' 
                      : 'bg-white text-[#1E3255] border-[#4B5675] hover:bg-[#F7F9FC]'
                    }
                  `}
                  style={{ zIndex: 20 - index }}
                  onClick={() => handleInFacilitySubStatusClick(subStatus.value)}
                >
                  {subStatus.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sub-Status Filters - Only show when Destination Warehouse is selected */}
      {activeStatus === 'destination_warehouse' && (
        <div className="px-8 mt-4 relative z-0">
          <div className="flex mt-[-30px] mb-8 ml-[100px] w-fit relative z-0">
            {destinationWarehouseSubStatuses.map((subStatus, index) => {
              const isSelected = activeDestinationWarehouseSubStatus === subStatus.value;
              const overlapClass = index === 0 ? '' : '-ml-6';
              
              return (
                <button
                  key={subStatus.value}
                  className={`${overlapClass} px-8 py-2.5 first:pl-7 last:pr-7 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-full border relative
                    ${isSelected 
                      ? 'bg-[#FFF8D6] text-[#D4A017] font-semibold border-[#F2BC00]' 
                      : 'bg-white text-[#1E3255] border-[#4B5675] hover:bg-[#F7F9FC]'
                    }
                  `}
                  style={{ zIndex: 20 - index }}
                  onClick={() => handleDestinationWarehouseSubStatusClick(subStatus.value)}
                >
                  {subStatus.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {activeStatus === 'distribution_center' && (
        <div className="px-8 mt-4 relative z-0">
          <div className="flex mt-[-30px] mb-8 ml-[265px] w-fit relative z-0">
            {distributionCenterSubStatuses.map((subStatus, index) => {
              const isSelected = activeDistributionCenterSubStatus === subStatus.value;
              const overlapClass = index === 0 ? '' : '-ml-6';

              return (
                <button
                  key={subStatus.value}
                  className={`${overlapClass} px-8 py-2.5 first:pl-7 last:pr-7 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-full border relative
                    ${isSelected 
                      ? 'bg-[#FFF8D6] text-[#D4A017] font-semibold border-[#F2BC00]' 
                      : 'bg-white text-[#1E3255] border-[#4B5675] hover:bg-[#F7F9FC]'
                    }
                  `}
                  style={{ zIndex: 20 - index }}
                  onClick={() => handleDistributionCenterSubStatusClick(subStatus.value)}
                >
                  {subStatus.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
} 